import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";

import { FeedPerson, FeedRunCard, FeedSignal } from "@signalist/shared-types";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { collapsedSignalText } from "@/utils/signals/collapsedSignalText";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { selectJobByProspectId } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { updateFeedSignalPriority } from "@/pages/Private/redux/feed/feed.slice";
import { useUpdateSignalPriorityMutation } from "@/pages/Private/redux/list/list.api";
import { useIgnoreTrackerSignalMutation } from "@/pages/Private/redux/signal/signal.api";
import { getRelativeTimeString } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline/signalTimelineUtils";

import { FeedUndoToast } from "./FeedUndoToast";
import { SignalAiCta } from "./SignalAiCta";
import { SignalRowExpand } from "./SignalRowExpand";
import { SourceChip } from "./SourceChip";
import { STRENGTH_OPTIONS, StrengthMenu } from "./StrengthMenu";
import { StrengthReasonModal } from "./StrengthReasonModal";
import {
	buildSignalFeedbackContext,
	getInitials,
	getStrengthDisplay,
	signalRowHasStateChip,
} from "./runCardUtils";

/** How long the strength-change undo toast stays up (D1). */
const UNDO_TOAST_MS = 6000;

/**
 * Scope pill: lead → prospect photo (fallback: 2-letter initials), company →
 * company logo (fallback: "CO", mirroring the industry "IN" badge), industry →
 * "IN" label. A broken image URL falls back the same way as a missing one.
 */
const ScopePill: FunctionComponent<{ card: FeedRunCard; signal: FeedSignal }> = ({
	card,
	signal,
}) => {
	const [hasImageError, setHasImageError] = useState(false);

	if (signal.signalCategory === "Lead update") {
		const person = card.people[0];

		return (
			<span
				className="grid h-[22px] w-[22px] shrink-0 place-items-center overflow-hidden rounded-md bg-light-blue text-[9px] font-bold text-brand-light"
				title={signal.signalCategory}
			>
				{person?.avatarUrl && !hasImageError ? (
					<img
						alt=""
						className="h-full w-full object-cover"
						src={person.avatarUrl}
						onError={() => setHasImageError(true)}
					/>
				) : (
					getInitials(person?.name)
				)}
			</span>
		);
	}

	if (signal.signalCategory === "Industry update") {
		return (
			<span
				className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md bg-purple-100 text-[9px] font-bold text-purple-800"
				title={signal.signalCategory}
			>
				IN
			</span>
		);
	}

	return (
		<span
			className="grid h-[22px] w-[22px] shrink-0 place-items-center overflow-hidden rounded-md bg-background-light text-[9px] font-bold text-text-secondary"
			title={signal.signalCategory ?? undefined}
		>
			{card.company.logoUrl && !hasImageError ? (
				<img
					alt=""
					className="h-full w-full object-cover"
					src={card.company.logoUrl}
					onError={() => setHasImageError(true)}
				/>
			) : (
				"CO"
			)}
		</span>
	);
};

interface SignalRowProps {
	card: FeedRunCard;
	signal: FeedSignal;
	/** The card's currently selected person — feedback context target (D5). */
	selectedPerson: FeedPerson | null;
	isExpanded: boolean;
	onToggleExpand: () => void;
}

/**
 * One signal one-liner: caret, strength pill with the hover quick-change menu
 * (instant PATCH + optimistic slice update + undo toast with optional reason,
 * D1), scope pill, type chip, the signal's summary (falling back to its title),
 * source chip and relative time. Expands into {@link SignalRowExpand}.
 */
export const SignalRow: FunctionComponent<SignalRowProps> = ({
	card,
	signal,
	selectedPerson,
	isExpanded,
	onToggleExpand,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	// The AI chip takes the row's right edge; the timestamp is dropped whenever a
	// state chip is showing so the two don't stack (mirrors SignalAiCta's gating).
	const job = useAppSelector((state) => selectJobByProspectId(state, selectedPerson?.prospectId));
	const hasStateChip = signalRowHasStateChip(job, signal.signalId);
	const [updatePriority] = useUpdateSignalPriorityMutation();
	const [sendFeedback, { isLoading: isReasonSending }] = useIgnoreTrackerSignalMutation();

	const [undoState, setUndoState] = useState<{
		previousPriority: string | null;
		newPriority: string;
	} | null>(null);
	const [isReasonOpen, setIsReasonOpen] = useState(false);
	const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// The toast auto-commits after its window (no server action needed — the
	// PATCH already happened on click).
	useEffect(() => {
		if (!undoState) {
			return;
		}

		undoTimerRef.current = setTimeout(() => setUndoState(null), UNDO_TOAST_MS);

		return () => {
			if (undoTimerRef.current) {
				clearTimeout(undoTimerRef.current);
			}
		};
	}, [undoState]);

	const handleStrengthSelect = (priority: string) => {
		if (priority === signal.priority) {
			return;
		}

		const previousPriority = signal.priority;

		// Optimistic pill + instant PATCH (D1); a failed PATCH rolls the pill back.
		dispatch(updateFeedSignalPriority({ signalId: signal.signalId, priority }));
		updatePriority({ trackerSignalResultId: signal.signalId, priority })
			.unwrap()
			.catch(() => {
				dispatch(
					updateFeedSignalPriority({ signalId: signal.signalId, priority: previousPriority })
				);
				setUndoState(null);
			});
		setUndoState({ previousPriority, newPriority: priority });
	};

	const handleUndo = () => {
		if (!undoState) {
			return;
		}

		const { previousPriority } = undoState;

		setUndoState(null);
		dispatch(updateFeedSignalPriority({ signalId: signal.signalId, priority: previousPriority }));

		// Legacy priorities can't round-trip through the PATCH enum — the slice
		// revert above still restores the pill.
		if (STRENGTH_OPTIONS.some((option) => option.priority === previousPriority)) {
			updatePriority({
				trackerSignalResultId: signal.signalId,
				priority: previousPriority as string,
			});
		}
	};

	const handleReasonSubmit = async ({ feedback }: { feedback: string }) => {
		await sendFeedback({
			signalId: signal.signalId,
			data: {
				action: "SCORE_CHANGE",
				feedback,
				...buildSignalFeedbackContext(card, selectedPerson),
			},
		}).unwrap();

		setIsReasonOpen(false);
	};

	const time = signal.publishedDate ?? signal.signalCollectedAt;

	// PREC-5037/5038: the collapsed line shows what the signal says (its summary), not only its
	// source. `line` wins when the API sends one that differs from the title.
	const collapsed = collapsedSignalText({ title: signal.title, summary: signal.summary });
	const hasCustomLine = !!signal.line && signal.line !== signal.title;
	const displayText = hasCustomLine ? signal.line : (collapsed.text ?? signal.title);
	const hoverText = hasCustomLine ? signal.line : (collapsed.fullText ?? signal.title);

	return (
		<div
			className={classNames(
				"rounded-lg",
				isExpanded ? "border border-border-light bg-gray-100" : ""
			)}
			data-feed-noselect="true"
			data-testid={`feed-signal-row-${signal.signalId}`}
		>
			<div
				className="group/sigrow flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-background-light"
				onClick={onToggleExpand}
			>
				<Icon
					className={classNames(
						"h-3 w-3 shrink-0 text-text-tertiary transition-transform",
						isExpanded ? "rotate-90" : ""
					)}
					icon={faChevronRight}
				/>
				<StrengthMenu
					priority={signal.priority}
					signalId={signal.signalId}
					onSelect={handleStrengthSelect}
				/>
				<ScopePill card={card} signal={signal} />
				{/* Collapsed text capped + truncated (mockup `.sig-title` max-width) with the source
				    hugging its right edge — the source stays visible and clickable next to the text. A
				    flex spacer pushes the timestamp / AI chip to the far right. */}
				<span
					className="min-w-0 max-w-[520px] shrink truncate text-[13px] text-text"
					title={hoverText ?? undefined}
				>
					{displayText}
				</span>
				<span className="shrink-0">
					<SourceChip signal={signal} />
				</span>
				<span className="min-w-2 flex-1" />
				{time && (
					<span
						className={classNames(
							"shrink-0 text-[11px] text-text-tertiary",
							// The AI chip takes the row's right edge: drop the timestamp so it reads
							// as a swap — always when a state chip shows, on hover for the idle CTA
							// (mockup `:has(.sig-ref-state) .sig-time` + `.sig-row:hover .sig-time`).
							hasStateChip ? "hidden" : "group-hover/sigrow:hidden"
						)}
					>
						{getRelativeTimeString(time)}
					</span>
				)}
				{/* P6 AI CTA: hover trigger or the row-seeded job mirror (D2/D4). In-flow at the row's
				    right (shrink-0) — reveals on hover / shows the job chip — so it never overlaps the
				    Source link the way an absolute overlay did. */}
				<span className="shrink-0" data-testid="feed-signal-ai-slot">
					<SignalAiCta card={card} person={selectedPerson} signal={signal} variant="row" />
				</span>
			</div>

			{isExpanded && (
				<SignalRowExpand card={card} selectedPerson={selectedPerson} signal={signal} />
			)}

			{undoState && (
				<FeedUndoToast
					message={t("feed.runCard.strength.changed", {
						score: getStrengthDisplay(undoState.newPriority).label,
					})}
					onAddReason={() => {
						setUndoState(null);
						setIsReasonOpen(true);
					}}
					onUndo={handleUndo}
				/>
			)}

			<StrengthReasonModal
				isOpened={isReasonOpen}
				isSubmitting={isReasonSending}
				onClose={() => setIsReasonOpen(false)}
				onSubmit={handleReasonSubmit}
			/>
		</div>
	);
};
